// src/pages/orders/components/shared/CityDeliverySection.jsx
// Extracted from CustomerInformationSection.jsx's city-picker portion (Stage 8) so it can be
// reused verbatim by the offline-sales courier-fulfilled order form.
import React, { useRef, useEffect } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import useCitySearch from '../../../../hooks/useCitySearch';
import styles from '../CustomerInformationSection.module.css';

const CityDeliverySection = ({ cityId, cities, error, isCalculatingDelivery, onCityChange }) => {
  const { t } = useLocalization();
  const cityResultsRef = useRef(null);

  const {
    searchTerm,
    filteredCities,
    selectedIndex,
    showResults,
    selectedCity,
    handleSearchChange,
    handleKeyDown,
    selectCity,
    clearSearch,
    hideResults,
    showResultsDropdown,
  } = useCitySearch(cities);

  const handleCitySelect = (city) => {
    const selectedCityData = selectCity(city);
    onCityChange(selectedCityData.id);
  };

  const handleCityKeyDown = (e) => {
    const selectedCityData = handleKeyDown(e);
    if (selectedCityData) {
      handleCitySelect(selectedCityData);
    }
  };

  // Keep the box in sync when cityId changes from outside (edit load, phone autofill and its revert)
  useEffect(() => {
    if (String(selectedCity?.id ?? '') === String(cityId ?? '')) return;
    const currentCity = cityId ? cities.find(city => String(city.id) === String(cityId)) : null;
    if (currentCity) {
      selectCity(currentCity);
    } else if (selectedCity && (!cityId || cities.length > 0)) {
      clearSearch();
    }
  }, [cityId, cities, selectedCity, selectCity, clearSearch]);

  // Auto-select while typing / after browser autofill when the text exactly names one city
  // and no other city name starts with it (so "Colombo" still lets the user pick "Colombo 10").
  useEffect(() => {
    const typed = (searchTerm || '').trim().toLowerCase();
    if (!typed || selectedCity?.name?.toLowerCase() === typed) return;
    const prefixMatches = cities.filter(city => city.name?.toLowerCase().startsWith(typed));
    if (prefixMatches.length === 1 && prefixMatches[0].name.toLowerCase() === typed) {
      handleCitySelect(prefixMatches[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, cities]);

  // Latest values for the delayed blur handler (avoids stale closure)
  const latestRef = useRef({});
  latestRef.current = { searchTerm, selectedCity, cities };

  // Reconcile typed/browser-autofilled text with the actual selection so the box never
  // shows a city that isn't selected.
  const handleCityBlur = () => {
    setTimeout(() => {
      hideResults();
      const { searchTerm: term, selectedCity: current, cities: allCities } = latestRef.current;
      const typed = (term || '').trim().toLowerCase();

      if (current && current.name?.toLowerCase() === typed) return;

      const matches = typed
        ? (allCities || []).filter(city => city.name?.toLowerCase() === typed)
        : [];
      if (matches.length === 1) {
        handleCitySelect(matches[0]);
      } else if (current) {
        selectCity(current);
      } else {
        clearSearch();
      }
    }, 200);
  };

  const renderCitySearchResults = () => {
    if (!showResults) return null;
    return (
      <div className={styles.citySearchResults} ref={cityResultsRef}>
        {filteredCities.length > 0 ? (
          <div className={styles.cityResultsList}>
            {filteredCities.map((city, index) => (
              <div
                key={city.id}
                className={`${styles.cityResultItem} ${index === selectedIndex ? styles.selected : ''}`}
                onClick={() => handleCitySelect(city)}
              >
                <div className={styles.cityInfo}>
                  <div className={styles.cityName}>{city.name}</div>
                  <div className={styles.cityDetails}>
                    {city.District?.name}, {city.District?.Province?.name}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : searchTerm.trim() ? (
          <div className={styles.noCityResults}><span>No cities found</span></div>
        ) : null}
      </div>
    );
  };

  return (
    <div className={styles.formGroup}>
      <label htmlFor="citySearch">
        {t('ecomOrders.city')} *
        {isCalculatingDelivery && <Loader2 className={styles.loadingIcon} size={13} />}
      </label>
      <div className={styles.citySearchWrapper}>
        <div className={styles.cityInputWrapper}>
          <Search className={styles.inputIcon} size={16} />
          <input
            type="text"
            id="citySearch"
            name="city-search-no-autofill"
            autoComplete="off"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={handleCityKeyDown}
            onFocus={showResultsDropdown}
            onBlur={handleCityBlur}
            placeholder={t('ecomOrders.searchCity')}
            className={error ? styles.error : ''}
            required
          />
          {searchTerm && (
            <button type="button" onClick={clearSearch} className={styles.clearCityButton}>
              <X size={14} />
            </button>
          )}
        </div>
        {renderCitySearchResults()}
      </div>
      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
};

export default CityDeliverySection;
